"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var lodash_1 = __importDefault(require("lodash"));
/**
 * @category geom
 */
var Envelope = /** @class */ (function () {
    function Envelope(minx, miny, maxx, maxy) {
        this.minx = minx;
        this.miny = miny;
        this.maxx = maxx;
        this.maxy = maxy;
    }
    Object.defineProperty(Envelope.prototype, "width", {
        get: function () {
            return Math.abs(this.maxx - this.minx);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Envelope.prototype, "height", {
        get: function () {
            return Math.abs(this.maxy - this.miny);
        },
        enumerable: true,
        configurable: true
    });
    Envelope.prototype.centroid = function () {
        return { x: (this.minx + this.maxx) * .5, y: (this.miny + this.maxy) * .5 };
    };
    Envelope.prototype.expand = function (envelope) {
        this.minx = Math.min(this.minx, envelope.minx);
        this.miny = Math.min(this.miny, envelope.miny);
        this.maxx = Math.max(this.maxx, envelope.maxx);
        this.maxy = Math.max(this.maxy, envelope.maxy);
    };
    Envelope.prototype.area = function () {
        var width = Math.abs(this.maxx - this.minx);
        var height = Math.abs(this.maxy - this.miny);
        return width * height;
    };
    Envelope.prototype.perimeter = function () {
        var width = Math.abs(this.maxx - this.minx);
        var height = Math.abs(this.maxy - this.miny);
        return width * 2 + height * 2;
    };
    Envelope.from = function (param) {
        var flatten = lodash_1.default.flattenDeep(param);
        if (flatten.length === 0) {
            throw new Error('Invalid coordinates. Must be an array of ICoordinates or numbers.');
        }
        var coordinates;
        if (typeof lodash_1.default.first(flatten) === 'number') {
            var flattenNumbers = flatten;
            coordinates = new Array();
            for (var i = 0; i < flattenNumbers.length; i += 2) {
                coordinates.push({ x: flattenNumbers[i], y: flattenNumbers[i + 1] });
            }
        }
        else {
            coordinates = param;
        }
        var _a = [
            Number.POSITIVE_INFINITY,
            Number.POSITIVE_INFINITY,
            Number.NEGATIVE_INFINITY,
            Number.NEGATIVE_INFINITY
        ], minx = _a[0], miny = _a[1], maxx = _a[2], maxy = _a[3];
        for (var _i = 0, coordinates_1 = coordinates; _i < coordinates_1.length; _i++) {
            var c = coordinates_1[_i];
            minx = Math.min(minx, c.x);
            miny = Math.min(miny, c.y);
            maxx = Math.max(maxx, c.x);
            maxy = Math.max(maxy, c.y);
        }
        return new Envelope(minx, miny, maxx, maxy);
    };
    Envelope.union = function (env1, env2) {
        var minx = Math.min(env1.minx, env2.minx);
        var miny = Math.min(env1.miny, env2.miny);
        var maxx = Math.max(env1.maxx, env2.maxx);
        var maxy = Math.max(env1.maxy, env2.maxy);
        return new Envelope(minx, miny, maxx, maxy);
    };
    Envelope.intersection = function (env1, env2) {
        if (Envelope.disjoined(env1, env2)) {
            return undefined;
        }
        var minx = Math.max(env1.minx, env2.minx);
        var maxx = Math.min(env1.maxx, env2.maxx);
        var miny = Math.max(env1.miny, env2.miny);
        var maxy = Math.min(env1.maxy, env2.maxy);
        return new Envelope(minx, miny, maxx, maxy);
    };
    Envelope.unionAll = function (envelopes) {
        var envelope = Envelope.init();
        envelopes.forEach(function (e) { return envelope.expand(e); });
        return envelope;
    };
    Envelope.disjoined = function (envelope1, envelope2) {
        if (envelope1 === undefined || envelope2 === undefined)
            return true;
        return envelope1.maxx < envelope2.minx || envelope1.minx > envelope2.maxx
            || envelope1.miny > envelope2.maxy || envelope1.maxy < envelope2.miny;
    };
    Envelope.contains = function (envelope1, envelope2) {
        if (envelope1 === undefined || envelope2 === undefined) {
            return false;
        }
        return envelope1.minx <= envelope2.minx && envelope1.maxx >= envelope2.maxx &&
            envelope1.miny <= envelope2.miny && envelope1.maxy >= envelope2.maxy;
    };
    Envelope.intersects = function (envelope1, envelope2) {
        if (envelope1 === undefined || envelope2 === undefined) {
            return false;
        }
        var p1 = { x: envelope1.minx, y: envelope1.miny };
        var p2 = { x: envelope1.maxx, y: envelope1.maxy };
        var q1 = { x: envelope2.minx, y: envelope2.miny };
        var q2 = { x: envelope2.maxx, y: envelope2.maxy };
        var min1 = Math.min(q1.x, q2.x);
        var max1 = Math.max(q1.x, q2.x);
        var min2 = Math.min(p1.x, p2.x);
        var max2 = Math.max(p1.x, p2.x);
        if (min2 > max1) {
            return false;
        }
        if (max2 < min1) {
            return false;
        }
        min1 = Math.min(q1.y, q2.y);
        max1 = Math.max(q1.y, q2.y);
        min2 = Math.min(p1.y, p2.y);
        max2 = Math.max(p1.y, p2.y);
        if (min2 > max1) {
            return false;
        }
        if (max2 < min1) {
            return false;
        }
        return true;
    };
    Envelope.equals = function (envelope1, envelope2, tolerance) {
        if (tolerance === void 0) { tolerance = 0; }
        if (envelope1 === undefined && envelope2 === undefined) {
            return true;
        }
        if (envelope1 === undefined || envelope2 === undefined) {
            return false;
        }
        return Math.abs(envelope1.minx - envelope2.minx) <= tolerance &&
            Math.abs(envelope1.miny - envelope2.miny) <= tolerance &&
            Math.abs(envelope1.maxx - envelope2.maxx) <= tolerance &&
            Math.abs(envelope1.maxy - envelope2.maxy) <= tolerance;
    };
    Envelope.init = function () {
        return new Envelope(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY);
    };
    Envelope.overlaps = function (envelope1, envelope2) {
        if (envelope1 === undefined || envelope2 === undefined) {
            return false;
        }
        if (((envelope1.minx >= envelope2.minx && envelope1.minx <= envelope2.maxx)
            || (envelope1.maxx >= envelope2.minx && envelope1.maxx <= envelope2.maxx)
            || (envelope2.minx >= envelope1.minx && envelope2.minx <= envelope1.maxx)
            || (envelope2.maxx >= envelope1.minx && envelope2.maxx <= envelope1.maxx))
            &&
                ((envelope1.miny >= envelope2.miny && envelope1.miny <= envelope2.maxy)
                    || (envelope1.maxy >= envelope2.miny && envelope1.maxy <= envelope2.maxy)
                    || (envelope2.miny >= envelope1.miny && envelope2.miny <= envelope1.maxy)
                    || (envelope2.maxy >= envelope1.miny && envelope2.maxy <= envelope1.maxy))) {
            return true;
        }
        return false;
    };
    return Envelope;
}());
exports.default = Envelope;
